import React from 'react';

import { OVERLAY_PRIORITY } from 'core/constants';
import { useAppContext } from 'core/hooks/useAppContext';

export type OverlayCoordinatorOptions = {
	id: string;
	priority?: number;
	isRequested?: boolean;
	managed?: boolean;
};

/**
 * @param id - the unique identifier for the overlay.
 * @param priority - the priority of the overlay. Lower numbers indicate higher priority.
 * @param isRequested - whether the overlay is currently requested to be open.
 * @param managed - whether the overlay is managed by the overlay coordinator.
 * @returns an object containing the active overlay state and methods to request and release the overlay.
 */
export const useOverlayCoordinator = ({
	id,
	priority = OVERLAY_PRIORITY.DEFAULT,
	isRequested = false,
	managed = false,
}: OverlayCoordinatorOptions) => {
	const { activeOverlayId, requestOverlayOpen, releaseOverlay } =
		useAppContext();
	const hasOverlayCoordinator =
		typeof requestOverlayOpen === 'function' &&
		typeof releaseOverlay === 'function';

	React.useEffect(() => {
		if (!managed || !hasOverlayCoordinator) return undefined;

		if (isRequested) {
			requestOverlayOpen?.({ id, priority });

			return () => releaseOverlay?.(id);
		}

		releaseOverlay?.(id);

		return undefined;
	}, [
		hasOverlayCoordinator,
		id,
		isRequested,
		managed,
		priority,
		releaseOverlay,
		requestOverlayOpen,
	]);

	return {
		isActiveOverlay:
			!managed || !hasOverlayCoordinator || activeOverlayId === id,
		activeOverlayId,
		requestOverlayOpen,
		releaseOverlay,
	};
};
